import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { listCaregiversApi, listDependentsApi, listIncomingPatientRequestsApi } from "@/portals/patient/api/familyApi";
import { getSocket } from "@/lib/socketClient";
import { useLiveRefreshFallback } from "@/shared/hooks/useLiveRefreshFallback";
import type { AppNotification } from "@/stores/notificationsStore";
import type { CaregiverAccess, Dependent, IncomingPatientAccessRequest } from "@/shared/types/family";

// Every notification type that means "something a caregiver did just
// changed what this patient should see" — receiving any of these live-
// refreshes this context instead of waiting for a manual page reload.
const RELEVANT_NOTIFICATION_TYPES: ReadonlySet<AppNotification["type"]> = new Set([
  "ACCESS_REQUEST_RECEIVED",
  "ACCESS_CATEGORY_REQUESTED",
  "CAREGIVER_INVITE_ACCEPTED",
  "CAREGIVER_INVITE_DECLINED",
]);

// Fetch-once-per-session, same shape as ParsedRecordsContext/MedicationsContext
// — refresh() bumps a token to retrigger the effect, so anything that creates a
// dependent or sends a caregiver invite can force an immediate refetch instead
// of the page needing a manual reload to see its own change.
interface FamilyContextValue {
  dependents: Dependent[];
  caregivers: CaregiverAccess[];
  /** Caregiver-initiated requests (Direction A) awaiting this patient's own
   * review — the reverse of the caregiver portal's own incoming invites
   * (see CaregiverPatientsContext.invites, Direction B). */
  incomingPatientRequests: IncomingPatientAccessRequest[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  /** The dependent whose records the patient is currently viewing — null means "my own". */
  activeDependent: Dependent | null;
  /** Switches which profile's dashboard is being viewed; pass null to switch back to "my own". */
  setActiveDependentId: (dependentId: string | null) => void;
}

const FamilyContext = createContext<FamilyContextValue | null>(null);

export function FamilyProvider({ children }: { children: ReactNode }) {
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [caregivers, setCaregivers] = useState<CaregiverAccess[]>([]);
  const [incomingPatientRequests, setIncomingPatientRequests] = useState<IncomingPatientAccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [activeDependentId, setActiveDependentId] = useState<string | null>(null);
  const activeDependent = dependents.find((dependent) => dependent.id === activeDependentId) ?? null;

  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);

  // Live refresh — no page reload needed when a caregiver sends/accepts/
  // declines a request while this page is already open.
  useEffect(() => {
    const socket = getSocket();
    const handleNotification = (incoming: AppNotification) => {
      if (RELEVANT_NOTIFICATION_TYPES.has(incoming.type)) refresh();
    };
    socket.on("notification:new", handleNotification);
    return () => {
      socket.off("notification:new", handleNotification);
    };
  }, [refresh]);

  // Safety net for when the socket above isn't actually connected for any
  // reason — see useLiveRefreshFallback's own docstring. Never the primary
  // mechanism, just a guarantee this can't silently go stale forever.
  useLiveRefreshFallback(refresh);

  // isLoading only ever goes true for the very first load — every later
  // refresh (from the user's own action, the live socket, or the fallback
  // poll/focus revalidation above) fetches silently and swaps the data in
  // once it arrives, with no loading flash. Consumers like
  // ManageCaregiversPage replace their ENTIRE page with a spinner while
  // isLoading is true, so flipping it back to true on every background
  // refresh — which is exactly what was happening before this — meant the
  // whole page visibly blanked out and reappeared every 60 seconds, or
  // every time a caregiver did anything. Real apps update in place; only
  // the one-time "nothing to show yet" case needs a full-page spinner.
  // A ref (not a length check on the fetched arrays) — a patient with
  // genuinely zero dependents/caregivers/requests must still count as
  // "loaded" after its first successful fetch, not be stuck re-showing the
  // spinner on every refresh forever.
  const hasLoadedOnce = useRef(false);
  useEffect(() => {
    let cancelled = false;
    if (!hasLoadedOnce.current) setIsLoading(true);

    Promise.all([listDependentsApi(), listCaregiversApi(), listIncomingPatientRequestsApi()])
      .then(([dependentsResult, caregiversResult, incomingRequestsResult]) => {
        if (cancelled) return;
        if (dependentsResult.error || caregiversResult.error || incomingRequestsResult.error) {
          setError(dependentsResult.error ?? caregiversResult.error ?? incomingRequestsResult.error);
          return;
        }
        setDependents(dependentsResult.data ?? []);
        setCaregivers(caregiversResult.data ?? []);
        setIncomingPatientRequests(incomingRequestsResult.data ?? []);
        setError(null);
        hasLoadedOnce.current = true;
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load your family & caregiver settings. Please try again later.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  return (
    <FamilyContext.Provider
      value={{
        dependents,
        caregivers,
        incomingPatientRequests,
        isLoading,
        error,
        refresh,
        activeDependent,
        setActiveDependentId,
      }}
    >
      {children}
    </FamilyContext.Provider>
  );
}

export function useFamily() {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error("useFamily must be used within FamilyProvider");
  return ctx;
}
